import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useAuth } from '@/features/auth'
import { useAlertBadgeCount } from '@/features/insights/hooks/use-insights'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  FileText,
  Activity,
  Settings,
  Shield,
  MoreHorizontal,
  X,
} from 'lucide-react'

const primaryItems = [
  { name: 'Home', href: '/dashboard', icon: LayoutDashboard, match: '/dashboard' },
  { name: 'Pacientes', href: '/patients', icon: Users, match: '/patients' },
  { name: 'Agenda', href: '/schedule', icon: CalendarDays, match: '/schedule' },
  { name: 'Alertas', href: '/insights', icon: Activity, match: '/insights' },
] as const

const overflowItems = [
  { name: 'Relatos', href: '/reports', icon: FileText, match: '/reports' },
  { name: 'Configurações', href: '/settings', icon: Settings, match: '/settings' },
  { name: 'Administração', href: '/admin', icon: Shield, match: '/admin', adminOnly: true },
] as const

export function BottomNav() {
  const router = useRouterState()
  const currentPath = router.location.pathname
  const { isAdmin } = useAuth()
  const { data: alertBadgeCount } = useAlertBadgeCount()
  const [isMoreOpen, setIsMoreOpen] = useState(false)

  const isMatch = (match: string) =>
    match === '/dashboard' ? currentPath === '/dashboard' : currentPath.startsWith(match)

  const visibleOverflow = overflowItems.filter((item) => !('adminOnly' in item && item.adminOnly) || isAdmin)
  const isOverflowActive = visibleOverflow.some((item) => isMatch(item.match))

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-1px_3px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-center justify-around px-1 pb-[env(safe-area-inset-bottom)]">
          {primaryItems.map((item) => {
            const Icon = item.icon
            const isActive = isMatch(item.match)

            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 py-2 pt-2.5 gap-0.5 transition-colors relative',
                  isActive ? 'text-teal-600' : 'text-gray-400 active:text-gray-600'
                )}
              >
                <div className="relative">
                  <Icon className={cn('w-5 h-5', isActive && 'text-teal-600')} />
                  {item.href === '/insights' && (alertBadgeCount ?? 0) > 0 && (
                    <span className="absolute -top-1.5 -right-2 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {alertBadgeCount! > 9 ? '9+' : alertBadgeCount}
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    'text-[10px] font-medium leading-tight',
                    isActive ? 'text-teal-600' : 'text-gray-400'
                  )}
                >
                  {item.name}
                </span>
                {isActive && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-teal-500 rounded-full" />
                )}
              </Link>
            )
          })}

          <button
            type="button"
            onClick={() => setIsMoreOpen(true)}
            className={cn(
              'flex flex-col items-center justify-center flex-1 py-2 pt-2.5 gap-0.5 transition-colors relative',
              isOverflowActive ? 'text-teal-600' : 'text-gray-400 active:text-gray-600'
            )}
            aria-label="Mais opções"
          >
            <MoreHorizontal className={cn('w-5 h-5', isOverflowActive && 'text-teal-600')} />
            <span
              className={cn(
                'text-[10px] font-medium leading-tight',
                isOverflowActive ? 'text-teal-600' : 'text-gray-400'
              )}
            >
              Mais
            </span>
            {isOverflowActive && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-teal-500 rounded-full" />
            )}
          </button>
        </div>
      </nav>

      {isMoreOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40 animate-in fade-in-0"
            onClick={() => setIsMoreOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl pb-[env(safe-area-inset-bottom)] animate-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className="text-sm font-semibold text-gray-900">Mais opções</span>
              <button
                type="button"
                onClick={() => setIsMoreOpen(false)}
                className="p-1 -mr-1 text-gray-400 active:text-gray-600"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-2 pb-2">
              {visibleOverflow.map((item) => {
                const Icon = item.icon
                const isActive = isMatch(item.match)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMoreOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                      isActive ? 'bg-teal-50 text-teal-700' : 'text-gray-700 active:bg-gray-100'
                    )}
                  >
                    <Icon className={cn('w-5 h-5', isActive ? 'text-teal-600' : 'text-gray-400')} />
                    <span className="text-sm font-medium">{item.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
